import { LIMITS, type AttachmentDto } from '@initiative/shared';
import { flaeche2d } from '../modules/bild/farbraum.js';
import { api, ApiError } from './api.js';
import type { OutboxAttachment } from './db.js';
import { stueckweiseHochladen, type Fortschritt, type StueckTransport } from './stueckUpload.js';
import { direktMaxFuer, grenzenLaden } from './uploadGrenzen.js';
import { videoBereinigen } from './videoMetadaten.js';

/**
 * Geht diese Datei in Stücken hoch?
 *
 * Ab `stueckUploadAb` (32 MiB) – dort lohnt das Fortsetzen – und immer, wenn
 * sie über der alten Grenze des Weges in einer Anfrage liegt. Kleines bleibt
 * beim alten Weg: eine Anfrage statt drei, und ältere Server kennen nur ihn.
 */
export function inStuecken(attachment: Pick<OutboxAttachment, 'kind'>, groesse: number): boolean {
  return (
    groesse > LIMITS.stueckUploadAb ||
    groesse > (direktMaxFuer(attachment.kind) ?? LIMITS.stueckUploadAb)
  );
}

/** Die Datei liegt nicht mehr vor (Neustart) – sie muss neu ausgewählt werden. */
export class DateiFehltError extends Error {
  constructor(name: string) {
    super(`„${name}“ muss neu ausgewählt werden, damit der Upload weitergehen kann`);
    this.name = 'DateiFehltError';
  }
}

/** Was die Outbox beim Hochladen mitgibt. */
export interface UploadKontext {
  /** Merkt sich die Sitzung, damit nach Abbruch oder Neustart fortgesetzt wird. */
  onSitzung?: (sitzungId: string) => void | Promise<void>;
  onFortschritt?: (fortschritt: Fortschritt) => void;
  signal?: AbortSignal;
}

/** Der echte Transport für `stueckweiseHochladen` – die API. */
export const apiTransport: StueckTransport = {
  anlegen: (eingabe) => api.media.stueckeAnlegen(eingabe),
  async stand(sitzungId) {
    try {
      return await api.media.stueckeStand(sitzungId);
    } catch (fehler) {
      // Abgelaufen und weggeräumt – dann eben neu.
      if (fehler instanceof ApiError && fehler.status === 404) return null;
      throw fehler;
    }
  },
  async senden(sitzungId, start, stueck, gesamt, signal, sha256) {
    await api.media.stueckSenden(sitzungId, start, stueck, gesamt, signal, sha256);
  },
  abschliessen: (sitzungId, metadaten) =>
    api.media.stueckeAbschliessen(sitzungId, { ...metadaten }),
  abbrechen: (sitzungId) => api.media.stueckeAbbrechen(sitzungId),
};

/**
 * Two-step upload:
 * 1. ask the API for a target (presigned PUT on R2/S3, or a direct POST when the
 *    server stores files locally),
 * 2. report the metadata so the attachment becomes usable in a message.
 */
/** Nach dieser Zeit gilt ein Upload als gescheitert statt weiter zu hängen. */
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Wie gross die eingebettete Vorschau wird – die längste Kante in Punkten.
 *
 * # Warum sie so viel grösser ist als vorher
 *
 * Sie stand auf 48 (Bilder) und 64 (Videos). Angezeigt wird sie in einem
 * Rahmen von `min(74vw, 320px)`, auf einem Telefon also rund 291 CSS-Punkte:
 * Das ist eine SECHSFACHE Vergrösserung, und bei dreifacher Gerätedichte das
 * Achtzehnfache. Genau das meldet der Anwender als „sehr unscharf".
 *
 * An drei Stellen ist dieses Bildchen ausserdem nicht bloss ein Platzhalter
 * für einen Augenblick, sondern das Endergebnis:
 *
 *   * Ein Video im Chat zeigt es als `poster` und lädt mit
 *     `preload="metadata"` nie ein echtes Einzelbild – bis jemand auf
 *     Abspielen tippt, IST das 64-Punkt-Bild das Aussehen des Videos.
 *   * In der Dateiliste und in der Auswahl vor dem Senden steht daneben gar
 *     kein scharfes Bild; es wird dort nie geladen.
 *   * Und offline ist es ohnehin das Einzige, was da ist.
 *
 * # Warum 160 und nicht mehr
 *
 * Die Vorschau ist eine data-URL und fährt in JEDER Nachrichtenliste mit; sie
 * liegt je Anhang in einer Textspalte der Datenbank. Nachgemessen: 160 Punkte
 * Kante sind rund 2,5 bis 3 kB, 320 Punkte schon knapp 8 kB – eine Chatseite
 * mit fünfzig Fotos trüge dann 400 kB Base64 mit sich herum. Die Grenze
 * (`LIMITS.previewDataUrlMax`) liegt bei 32 000 Zeichen; davon waren bisher
 * vier Prozent benutzt, jetzt rund ein Zehntel.
 *
 * Bei 160 Punkten fällt die Vergrösserung im Chat von 6,1 auf 1,8 – und
 * darüber liegt ohnehin noch der gewollte Weichzeichner der Überblendung.
 */
const VORSCHAU_KANTE = 160;

/**
 * Und die Güte.
 *
 * 0,5 war für 48 Punkte gedacht, wo ohnehin nichts zu erkennen war. Auf 160
 * Punkten sieht man die Blockartefakte; 0,62 kostet wenige hundert Byte und
 * nimmt ihnen die Kanten.
 */
const VORSCHAU_GUETE = 0.62;

export async function uploadBlob(
  attachment: OutboxAttachment,
  kontext: UploadKontext = {},
): Promise<AttachmentDto> {
  if (attachment.blobFehlt) throw new DateiFehltError(attachment.fileName);
  // Die Grenzen des Servers einmal je Sitzung – schlägt das fehl, gelten die
  // Konstanten; ein Upload darf daran nie scheitern.
  await grenzenLaden(api.media.grenzen);
  /*
   * Der Vorposten für Video-Metadaten – hier und nirgends sonst.
   *
   * In die App kommt ein Video auf sechs Wegen: Galerie, Aufnahme in der
   * App, der Kamera-Rückfall über `capture`, das Teilen-Ziel, der
   * Datei-Anhang und das Hochladen in eine Sammlung oder an einen Termin.
   * Vier davon können Ortsdaten tragen – die Aufnahme im Browser nicht, ein
   * Griff in die Kamerarolle sehr wohl. Sie alle laufen durch diese eine
   * Funktion; jeden Weg einzeln zu behandeln hiesse, beim nächsten den
   * Vorposten zu vergessen.
   *
   * Nur bei Video, nicht bei jedem Blob: Eine Sprachnachricht hat nichts zu
   * verbergen, und die Suche nach dem Boxbaum liefe sonst über jede einzelne.
   *
   * Schlägt das Bereinigen fehl, geht die Datei UNVERÄNDERT hoch. Das ist
   * eine bewusste Abwägung: Ein Anhang, der wegen eines unbekannten
   * Containers gar nicht ankommt, ist ein Fehler, den der Anwender sofort
   * bemerkt und nicht versteht.
   */
  if (attachment.kind === 'video' || attachment.mime.startsWith('video/')) {
    try {
      const { datei, befund } = await videoBereinigen(attachment.blob);
      if (befund.geaendert) attachment = { ...attachment, blob: datei };
    } catch {
      /* unbekannter Container – dann eben unverändert */
    }
  }

  if (inStuecken(attachment, attachment.blob.size)) {
    return stueckweiseHochladen({
      datei: attachment.blob,
      kind: attachment.kind,
      mime: attachment.mime,
      fileName: attachment.fileName,
      metadaten: {
        width: attachment.width,
        height: attachment.height,
        durationMs: attachment.durationMs,
        waveform: attachment.waveform,
        previewDataUrl: attachment.previewDataUrl,
      },
      transport: apiTransport,
      sitzungId: attachment.stueckSitzung ?? null,
      onSitzung: kontext.onSitzung,
      onFortschritt: kontext.onFortschritt,
      signal: kontext.signal,
    });
  }

  const target = await api.media.createUpload({
    kind: attachment.kind,
    mime: attachment.mime,
    size: attachment.blob.size,
    fileName: attachment.fileName,
  });

  if (target.strategy === 'presigned') {
    let response: Response;
    try {
      response = await fetch(target.uploadUrl, {
        method: 'PUT',
        headers: target.headers,
        body: attachment.blob,
        // Ohne Zeitlimit bleibt ein hängender Upload für immer „wird
        // gesendet“ – die Nachricht liess sich dann weder senden noch
        // loswerden.
        signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new Error(
          `Upload abgebrochen: Der Speicher hat innerhalb von ${Math.round(UPLOAD_TIMEOUT_MS / 1000)} Sekunden nicht geantwortet.`,
        );
      }
      // Der Browser lädt hier direkt in den Bucket. Fehlt dort die CORS-Regel
      // für diese Domain, bricht er ohne Statuscode ab – die blanke
      // fetch-Meldung ("Failed to fetch") sagt niemandem, was zu tun ist.
      throw new Error(
        'Upload zum Speicher blockiert. Meist fehlt im Bucket die CORS-Regel für ' +
          `${window.location.origin} (AllowedMethods PUT und GET, AllowedHeaders content-type). ` +
          `Ursprüngliche Meldung: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
    if (!response.ok) throw new Error(`Upload fehlgeschlagen (${response.status})`);
    return api.media.completeUpload(target.attachmentId, {
      width: attachment.width,
      height: attachment.height,
      durationMs: attachment.durationMs,
      waveform: attachment.waveform,
      previewDataUrl: attachment.previewDataUrl,
    });
  }

  await api.media.uploadData(target.attachmentId, attachment.blob, attachment.fileName);
  return api.media.completeUpload(target.attachmentId, {
    width: attachment.width,
    height: attachment.height,
    durationMs: attachment.durationMs,
    waveform: attachment.waveform,
    previewDataUrl: attachment.previewDataUrl,
  });
}

export interface PreparedImage {
  blob: Blob;
  mime: string;
  width: number;
  height: number;
  previewDataUrl: string;
}

async function loadBitmap(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(blob);
    } catch {
      /* fall through to the <img> path (older iOS, HEIC) */
    }
  }
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(blob);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Bild konnte nicht gelesen werden'));
    };
    image.src = url;
  });
}

function drawTo(
  source: ImageBitmap | HTMLImageElement,
  width: number,
  height: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  /*
   * Im Arbeitsraum, nicht in sRGB.
   *
   * Hier wird ein Foto neu kodiert, bevor es hochgeht. Läge diese eine
   * Leinwand in sRGB, wäre der weite Farbraum genau hier verloren – nachdem
   * der Editor ihn die ganze Kette hindurch gehalten hat, und ohne dass
   * irgendwo etwas danach aussähe. Siehe `modules/bild/farbraum.ts`.
   */
  const context = flaeche2d(canvas);
  if (!context) throw new Error('Canvas nicht verfügbar');
  context.imageSmoothingQuality = 'high';
  context.drawImage(source as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Bild konnte nicht kodiert werden'))),
      mime,
      quality,
    );
  });
}

async function supportsWebp(): Promise<boolean> {
  if (webpSupport != null) return webpSupport;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  return webpSupport;
}
let webpSupport: boolean | null = null;

/**
 * Downscale a photo before upload and build the tiny inline preview that makes
 * chats render instantly (and readable offline).
 */
export async function prepareImage(
  file: Blob,
  maxDimension = 1920,
  /**
   * Das Bild ist bereits fertig – nur die Vorschau soll noch entstehen.
   *
   * Für alles, was aus dem Bild-Editor kommt. Dort ist schon einmal
   * verlustbehaftet kodiert worden (WebP 0,92); es hier ein zweites Mal durch
   * WebP 0,82 zu schicken, kostet Güte, ohne irgendetwas zu gewinnen. Bei
   * einem Bild, das aus der Kamera kam, sind das drei Kodierungen
   * hintereinander – jede baut auf den Artefakten der vorigen auf.
   */
  fertig = false,
): Promise<PreparedImage> {
  const source = await loadBitmap(file);
  const naturalWidth = 'width' in source ? source.width : 0;
  const naturalHeight = 'height' in source ? source.height : 0;
  /*
   * `fertig` hebt die Kantengrenze NICHT auf.
   *
   * Es hiess einmal „Grösse egal, nicht anfassen“, und damit war es für die
   * beiden Wege aus dem Bild-Editor unbrauchbar: Die hätten dann bis zu 2560
   * Punkte Kante in den Chat geschickt, wo sonst 1920 gelten. Also blieb es
   * dort aus – und das Bild lief ein zweites Mal durch WebP 0,82, obwohl der
   * Editor schon mit 0,92 kodiert hatte.
   *
   * Richtig ist die Unterscheidung dazwischen: Passt das fertige Bild in die
   * Grenze, geht es unangetastet durch. Passt es nicht, muss ohnehin neu
   * gezeichnet werden – dann aber mit der Güte der zweiten Generation und
   * nicht mit der für ein Kameraoriginal gedachten.
   */
  const scale = Math.min(1, maxDimension / Math.max(naturalWidth, naturalHeight || 1));
  const width = Math.round(naturalWidth * scale);
  const height = Math.round(naturalHeight * scale);
  const unberuehrt = fertig && scale === 1;

  const mime = unberuehrt
    ? file.type || 'image/webp'
    : (await supportsWebp())
      ? 'image/webp'
      : 'image/jpeg';
  const full = unberuehrt
    ? file
    : await toBlob(drawTo(source, width, height), mime, fertig ? 0.92 : 0.82);

  const previewScale = Math.min(1, VORSCHAU_KANTE / Math.max(width, height || 1));
  const preview = drawTo(source, width * previewScale, height * previewScale);
  const previewDataUrl = preview.toDataURL('image/jpeg', VORSCHAU_GUETE);

  if ('close' in source) source.close();
  return { blob: full, mime, width, height, previewDataUrl };
}

/** First frame of a video, used as poster image and offline preview. */
export async function videoPreview(
  file: Blob,
): Promise<{ previewDataUrl: string; width: number; height: number; durationMs: number } | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let settled = false;

    const finish = (value: Awaited<ReturnType<typeof videoPreview>>) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(value);
    };

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    video.onloadeddata = () => {
      try {
        video.currentTime = Math.min(0.1, (video.duration || 1) / 4);
      } catch {
        finish(null);
      }
    };
    video.onseeked = () => {
      try {
        const scale = Math.min(
          1,
          VORSCHAU_KANTE / Math.max(video.videoWidth, video.videoHeight || 1),
        );
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish({
          previewDataUrl: canvas.toDataURL('image/jpeg', VORSCHAU_GUETE),
          width: video.videoWidth,
          height: video.videoHeight,
          durationMs: Math.round((video.duration || 0) * 1000),
        });
      } catch {
        finish(null);
      }
    };
    video.onerror = () => finish(null);
    setTimeout(() => finish(null), 8000);
  });
}

/** Normalised peaks (0..1) for the voice-message waveform. */
export async function waveformFromBlob(blob: Blob, buckets = 64): Promise<number[]> {
  const AudioCtor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return [];
  const context = new AudioCtor();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    const channel = buffer.getChannelData(0);
    const size = Math.floor(channel.length / buckets) || 1;
    const peaks: number[] = [];
    for (let i = 0; i < buckets; i += 1) {
      let peak = 0;
      for (let j = 0; j < size; j += 1) {
        const value = Math.abs(channel[i * size + j] ?? 0);
        if (value > peak) peak = value;
      }
      peaks.push(peak);
    }
    const max = Math.max(...peaks, 0.001);
    return peaks.map((peak) => Math.min(1, Number((peak / max).toFixed(3))));
  } catch {
    return [];
  } finally {
    void context.close();
  }
}
